import api, { APIError } from './api';
import User, { Driver, UserInterface } from './user';
import Product, { ProductInterface } from './product';
import { toIsoString } from '@/utils/utilities';
export interface OrderInterface {
    id?: number;
    is_picked_up: boolean;
    scheduled_time: Date | string;
    status: OrderStatus;
    address: string;
    business_name: string;
    delivery_instructions: string;
    delivery_fee?: number;
    user?: UserInterface;
    products: ProductInterface[];
    options: Record<string, boolean>;
    total: number;
    tip_amount?: number;
    payment_option?: PaymentOption;
    driver?: Driver;
    delayed_time?: number;
    tag?: OrderTag | null;
    status_updated_at?: Date;
    created_at?: Date;
    currency?: string;
    order_placed_at?: Date;
}

export interface OrderTag {
    id: number;
    name: string;
    color: string;
}

export enum OrderStatus {
    CANCEL = 'cancel',
    PENDING = 'pending',
    CONFIRMED = 'confirmed',
    IN_PROGRESS = 'in_progress',
    DONE = 'done',
}

export enum PaymentOption {
    CASH = 'cash',
    CARD = 'card',
}

export type CreditCard = {
    number: string;
    exp_date: string;
    cvv: string;
};

export type PaymentParameters =
    | {
          cc_infos: CreditCard;
          amount: number;
      }
    | {
          membership_applied: boolean;
          token: string;
      };

export default class Order implements OrderInterface {
    id?: number;
    is_picked_up: boolean;
    scheduled_time: Date;
    status: OrderStatus;
    address: string;
    business_name: string;
    delivery_instructions: string;
    delivery_fee?: number;
    user?: User;
    products: Product[];
    options: Record<string, boolean>;
    total: number;
    tip_amount?: number;
    payment_option?: PaymentOption;
    driver?: Driver;
    delayed_time?: number;
    tag?: OrderTag | null;
    status_updated_at?: Date;
    created_at?: Date;
    currency?: string;
    order_placed_at?: Date;

    constructor(order: OrderInterface) {
        this.id = order.id;
        this.is_picked_up = order.is_picked_up;
        this.scheduled_time = new Date(order.scheduled_time);
        this.status = order.status;
        this.address = order.address;
        this.business_name = order.business_name;
        this.delivery_instructions = order.delivery_instructions;
        this.total = order.total;
        this.options = order.options || {};
        this.products = Product.createBatch(order.products);

        if (order.user !== undefined) {
            this.user = new User(order.user);
        }
        if (order.payment_option !== undefined) {
            this.payment_option = order.payment_option;
        }
        if (order.delivery_fee !== undefined) {
            this.delivery_fee = order.delivery_fee;
        }
        if (order.driver !== undefined) {
            this.driver = order.driver;
        }
        if (order.tip_amount !== undefined) {
            this.tip_amount = order.tip_amount;
        }
        if (order.delayed_time !== undefined) {
            this.delayed_time = order.delayed_time;
        }
        if (order.tag !== undefined) {
            this.tag = order.tag;
        }
        if (order.status_updated_at !== undefined) {
            this.status_updated_at = order.status_updated_at;
        }
        if (order.order_placed_at !== undefined) {
            this.order_placed_at = order.order_placed_at;
        }
        if (order.created_at !== undefined) {
            this.created_at = order.created_at;
        }
        if (order.currency !== undefined) {
            this.currency = order.currency;
        }
    }

    static async find(id: number): Promise<Order | null> {
        const response = await api.get<OrderInterface>({ name: 'order.show', parameters: { id } });
        if (response.success) {
            return new Order(response.content as OrderInterface);
        }
        return null;
    }

    static async all(date: Date = new Date()): Promise<Order[]> {
        const response = await api.get<OrderInterface[]>({ name: 'order.index', parameters: { date: toIsoString(date) } });
        if (response.success) {
            return Order.createBatch(<OrderInterface[]>response.content);
        }
        return [];
    }

    static async past(date: Date): Promise<Order[]> {
        const response = await api.get<OrderInterface[]>({ name: 'order.past', parameters: { date: toIsoString(date) } });
        if (response.success) {
            return Order.createBatch(<OrderInterface[]>response.content);
        }
        return [];
    }

    public async update(): Promise<boolean> {
        const response = await api.post<null>(
            { name: 'order.update', parameters: { id: this.id } },
            {
                id: this.id,
                is_picked_up: this.is_picked_up,
                scheduled_time: this.scheduled_time,
                status: this.status,
                address: this.address,
                business_name: this.business_name,
                delivery_instructions: this.delivery_instructions,
                delivery_fee: this.delivery_fee,
                user: this.user,
                products: this.products,
                options: this.options,
                tip_amount: this.tip_amount,
                total: this.total,
                payment_option: this.payment_option,
                driver: this.driver,
                delayed_time: this.delayed_time,
                tag: this.tag,
            }
        );
        return response.success;
    }

    public async save(): Promise<Order | null> {
        const response = await api.post<OrderInterface>(
            { name: 'order.create' },
            {
                is_picked_up: this.is_picked_up,
                scheduled_time: this.scheduled_time,
                status: this.status,
                address: this.address,
                business_name: this.business_name,
                delivery_instructions: this.delivery_instructions,
                delivery_fee: this.delivery_fee,
                user: this.user,
                products: this.products,
                options: this.options,
                payment_option: this.payment_option,
                tip_amount: this.tip_amount,
                total: this.total,
            }
        );
        if (response.success) {
            return new Order(response.content as OrderInterface);
        }
        return null;
    }

    public async payment(paymentParameters: PaymentParameters): Promise<any | null> {
        const response = await api.post<any>({ name: 'order.payment', parameters: { id: this.id } }, paymentParameters);
        if (response) {
            return response;
        }
        return null;
    }

    public async transactions() {
        const response = await api.get<any>({ name: 'order.transactions', parameters: { id: this.id } });
        if (response.success) {
            return response;
        }
        return null;
    }

    static async getOrderCardType(order: Order) {
        const response = await order.transactions();
        if (response) {
            const authorizedTransaction = response.content.filter((transaction: any) => transaction.status === 'authorized');
            if (authorizedTransaction[0] && authorizedTransaction[0].credit_card_type) {
                return authorizedTransaction[0].credit_card_type;
            }
        }
        return '';
    }

    public static createBatch(orders: OrderInterface[] = []) {
        let ordersInstances = [];
        for (let index = 0; index < orders.length; index++) {
            ordersInstances.push(new Order(orders[index]));
        }
        return ordersInstances as Order[];
    }

    public static async count(date: Date = new Date(), status: OrderStatus = OrderStatus.DONE): Promise<number> {
        const response = await api.get<number>({ name: 'order.count', parameters: { date: toIsoString(date), status: status } });
        if (response.success) {
            return response.content as number;
        }
        return 0;
    }
}
